import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { AlertTriangle, DollarSign, X, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

const SESSION_KEY = "kloss_unpaid_alert_dismissed";

function fmtDateBR(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
}

function moneyBRL(v) {
  return (v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

export function UnpaidConsultationsAlert() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    const dismissedFor = sessionStorage.getItem(SESSION_KEY);
    if (dismissedFor === user.id) return;

    api.get("/consultations/unpaid-attended")
      .then(({ data }) => {
        if (data?.length > 0) {
          setItems(data);
          setOpen(true);
        }
      })
      .catch(() => {});
  }, [user]);

  function dismiss() {
    setOpen(false);
    if (user?.id) sessionStorage.setItem(SESSION_KEY, user.id);
  }

  function goToPatient(patientId) {
    dismiss();
    navigate(`/pacientes/${patientId}`);
  }

  if (!open || items.length === 0) return null;

  const totalPending = items.reduce((sum, i) => sum + (i.amount || 0), 0);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#0B2230]/50 backdrop-blur-sm animate-fade-in" data-testid="unpaid-alert-overlay" onClick={dismiss}>
      <div className="relative w-full max-w-lg mx-4 bg-white rounded-2xl shadow-2xl border border-[#E6E1DC] overflow-hidden animate-scale-in" onClick={(e) => e.stopPropagation()} data-testid="unpaid-alert-modal">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 bg-gradient-to-r from-[#FEF2F2] to-[#FFFBEB] border-b border-[#FECACA]">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#DC2626]/10">
            <AlertTriangle className="h-5 w-5 text-[#DC2626]" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-bold text-[#0B2230]" data-testid="unpaid-alert-title">Consultas sem pagamento registrado</h2>
            <p className="text-xs text-[#92400E] mt-0.5">{items.length} consulta{items.length > 1 ? "s" : ""} realizada{items.length > 1 ? "s" : ""} pendente{items.length > 1 ? "s" : ""} de pagamento</p>
          </div>
          <button onClick={dismiss} className="flex h-8 w-8 items-center justify-center rounded-lg text-[#8C98A0] hover:bg-[#F4F1EA] hover:text-[#0B2230] transition-colors" data-testid="unpaid-alert-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* List */}
        <div className="max-h-[340px] overflow-y-auto">
          {items.map((item) => (
            <button key={item.id} onClick={() => goToPatient(item.patient_id)} className="flex w-full items-center gap-3 px-5 py-3 text-left border-b border-[#F4F1EA] last:border-b-0 hover:bg-[#F7F5F2] transition-colors group" data-testid={`unpaid-alert-item-${item.id}`}>
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#FEF2F2] flex-shrink-0">
                <DollarSign className="h-4 w-4 text-[#DC2626]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#0B2230] truncate">{item.patient_name}</div>
                <div className="text-xs text-[#6F7C83] mt-0.5 flex items-center gap-2 flex-wrap">
                  <span>{item.consultation_type_label}</span>
                  <span className="text-[#DCD7CE]">|</span>
                  <span>{fmtDateBR(item.date)}{item.time ? ` ${item.time}` : ""}</span>
                  {item.professional_name && <><span className="text-[#DCD7CE]">|</span><span>{item.professional_name}</span></>}
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <span className="text-sm font-bold text-[#DC2626]">{moneyBRL(item.amount)}</span>
                <ChevronRight className="h-4 w-4 text-[#DCD7CE] group-hover:text-[#0B2230] transition-colors" />
              </div>
            </button>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 bg-[#F7F5F2] border-t border-[#E6E1DC]">
          <div className="text-xs text-[#5C6B73]">Total pendente: <span className="font-bold text-[#DC2626]">{moneyBRL(totalPending)}</span></div>
          <button onClick={dismiss} className="px-4 py-2 rounded-lg bg-[#0B2230] text-white text-xs font-semibold hover:bg-[#1E445B] transition-colors" data-testid="unpaid-alert-dismiss">
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
}
